/**
 * LocationResolver Service
 * Resolves location codes (province, district, municipality) to human-readable names
 * Data source: /public/json/location.json
 */

let locationData = null;

const loadLocationData = async () => {
  if (locationData) return locationData;

  try {
    const response = await fetch('/json/location.json');
    if (!response.ok) throw new Error('Failed to load location data');
    const data = await response.json();
    locationData = data.item;
    return locationData;
  } catch (error) {
    console.error('Error loading location data:', error);
    return null;
  }
};

export const LocationResolver = {
  // Expose locationData for component access (read-only pattern)
  get _locationData() {
    return locationData;
  },

  /**
   * Get province name by code
   * @param {number|string} provinceCode - Province code (1-7)
   * @param {string} format - 'english', 'nepali', or 'both' (default: 'both')
   * @returns {string} Province name(s)
   */
  getProvinceName: (provinceCode, format = 'both') => {
    if (!locationData || !provinceCode) return null;
    const code = parseInt(provinceCode, 10);
    const province = locationData.provinces?.find(p => p.code === code);
    if (!province) return null;

    if (format === 'english') return province.label;
    if (format === 'nepali') return province.alt_name;
    return `${province.label} - ${province.alt_name}`;
  },

  /**
   * Get district name by code
   * @param {number|string} districtCode - District code
   * @param {string} format - 'english', 'nepali', or 'both' (default: 'both')
   * @returns {string} District name(s)
   */
  getDistrictName: (districtCode, format = 'both') => {
    if (!locationData || !districtCode) return null;
    const code = parseInt(districtCode, 10);

    for (const province of locationData.provinces || []) {
      const district = province.districts?.find(d => d.code === code);
      if (district) {
        if (format === 'english') return district.label;
        if (format === 'nepali') return district.alt_name;
        return `${district.label} - ${district.alt_name}`;
      }
    }
    return null;
  },

  /**
   * Get municipality name by code
   * @param {number|string} municipalityCode - Municipality code
   * @param {string} format - 'english', 'nepali', or 'both' (default: 'both')
   * @returns {string} Municipality name(s)
   */
  getMunicipalityName: (municipalityCode, format = 'both') => {
    if (!locationData || !municipalityCode) return null;
    const code = parseInt(municipalityCode, 10);

    for (const province of locationData.provinces || []) {
      for (const district of province.districts || []) {
        const municipality = district.municipalities?.find(m => m.code === code);
        if (municipality) {
          if (format === 'english') return municipality.label;
          if (format === 'nepali') return municipality.alt_name;
          return `${municipality.label} - ${municipality.alt_name}`;
        }
      }
    }
    return null;
  },

  /**
   * Get all location names for a complete address
   * @param {object} address - { province, district, municipality } (codes as number or string)
   * @param {string} format - 'english', 'nepali', or 'both' (default: 'both')
   * @returns {object} { province, district, municipality } with resolved names
   */
  resolveAddress: (address, format = 'both') => {
    if (!address) return null;

    return {
      province: address.province ? LocationResolver.getProvinceName(parseInt(address.province, 10), format) : null,
      district: address.district ? LocationResolver.getDistrictName(parseInt(address.district, 10), format) : null,
      municipality: address.municipality ? LocationResolver.getMunicipalityName(parseInt(address.municipality, 10), format) : null,
    };
  },

  /**
   * Initialize location data (should be called on app startup)
   * @returns {Promise<void>}
   */
  initialize: async () => {
    await loadLocationData();
  },

  /**
   * Check if location data is loaded
   * @returns {boolean}
   */
  isLoaded: () => {
    return locationData !== null;
  },
};

// Auto-initialize on first use
export const useLocationResolver = () => {
  const initialized = { current: false };

  const ensureLoaded = async () => {
    if (!initialized.current && !LocationResolver.isLoaded()) {
      await LocationResolver.initialize();
      initialized.current = true;
    }
  };

  return {
    ...LocationResolver,
    ensureLoaded,
  };
};
